import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { clampPlayerLevel, PLAYER_BRIGHTNESS_NEUTRAL_LEVEL } from '@/utils/playerLevels'

export type ThemeMode = 'dark' | 'system' | 'light'

interface SettingsData {
    theme: ThemeMode
    playerVolume: number
    playerBrightness: number
    preferredSource: string
}

const STORAGE_KEY = 'cineflow:settings:v1'
const defaults: SettingsData = { theme: 'system', playerVolume: 0.35, playerBrightness: PLAYER_BRIGHTNESS_NEUTRAL_LEVEL, preferredSource: '' }

function load(): SettingsData {
    try {
        const candidate: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
        const parsed = candidate && typeof candidate === 'object'
            ? candidate as Partial<SettingsData> & { playerBrightnessModel?: string }
            : {}
        const settings = { ...defaults, ...parsed }
        if (parsed.playerBrightnessModel !== 'extended-v1') {
            settings.playerBrightness = clampPlayerLevel(Number(parsed.playerBrightness ?? 1)) * PLAYER_BRIGHTNESS_NEUTRAL_LEVEL
        }
        return settings
    }
    catch {
        return { ...defaults }
    }
}

export const useSettingsStore = defineStore('settings', () => {
    const saved = load()
    const theme = ref<ThemeMode>(saved.theme)
    const playerVolume = ref(clampPlayerLevel(Number(saved.playerVolume)))
    const playerBrightness = ref(clampPlayerLevel(Number(saved.playerBrightness)))
    const preferredSource = ref(saved.preferredSource)

    const systemTheme = ref<'dark' | 'light'>('dark')
    let mediaQuery: MediaQueryList | undefined

    function resolveTheme() {
        return theme.value === 'system' ? systemTheme.value : theme.value
    }

    function applyTheme() {
        document.documentElement.dataset.theme = resolveTheme()
    }

    function initializeTheme() {
        mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        systemTheme.value = mediaQuery.matches ? 'dark' : 'light'
        mediaQuery.addEventListener('change', (event) => {
            systemTheme.value = event.matches ? 'dark' : 'light'
            applyTheme()
        })
        applyTheme()
    }

    watch([theme, playerVolume, playerBrightness, preferredSource], () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ theme: theme.value, playerVolume: playerVolume.value, playerBrightness: playerBrightness.value, playerBrightnessModel: 'extended-v1', preferredSource: preferredSource.value }))
        applyTheme()
    })
    return { theme, systemTheme, playerVolume, playerBrightness, preferredSource, applyTheme, initializeTheme, resolveTheme }
})
